import { Box } from '@mui/material'
import { ColDef } from 'ag-grid-community'
import { getUserHistory } from 'api/users'
import columnRenderers from 'components/column-renderers'
import updatedByOrgRenderer from 'components/column-renderers/updatedByOrg'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { useGridControl } from 'hooks/useGridControl'
import { userCellValueRenderer, userFieldNameRenderer } from 'models/user.model'
import { useCallback, useEffect, useMemo } from 'react'
import { BaseTableRequest, EOp, Pathway, TableResponse } from 'types'
import { useGetDataSource } from 'utils'

export function UserHistory({
  userId,
  pathways,
  needUpdate,
  resetNeedUpdate,
}: {
  userId: number
  needUpdate: boolean
  pathways?: TableResponse<Pathway>
  resetNeedUpdate: () => void
}) {
  const getUserHistoryCallback = useCallback(
    (params: BaseTableRequest) => {
      const sort = params.sort || { field: 'updatedAt', sort: 'desc' }
      return getUserHistory({
        ...params,
        filter: {
          ...(params.filter || {}),
          userId: {
            type: 'number',
            op: EOp.equals,
            value: userId,
          },
        },
        sort,
      })
    },
    [userId],
  )

  const getDataSource = useGetDataSource(getUserHistoryCallback)

  const { gridRef, onGridReady } = useGridControl(getDataSource)

  const columnDefs: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        headerName: 'Action',
        field: 'updateAction',
        maxWidth: 120,
        cellRenderer: columnRenderers.capitalizeRenderer('updateAction'),
      },
      {
        headerName: 'Field',
        field: 'fieldName',
        cellRenderer: userFieldNameRenderer('fieldName'),
        minWidth: 140,
      },
      {
        headerName: 'Old Value',
        field: 'oldValue',
        cellRenderer: userCellValueRenderer('oldValue', pathways),
        minWidth: 300,
      },
      {
        headerName: 'New Value',
        field: 'newValue',
        cellRenderer: userCellValueRenderer('newValue', pathways),
        minWidth: 300,
      },
      { headerName: 'Pathway', field: 'trainingPathwayTypeTitle', minWidth: 300, hide: !pathways },
      { headerName: 'Date', field: 'updatedAt', cellRenderer: columnRenderers.date('updatedAt') },
      {
        headerName: 'Updated by',
        field: 'creator',
        cellRenderer: updatedByOrgRenderer,
        minWidth: 450,
      },
    ]
    return columns
  }, [pathways])

  useEffect(() => {
    if (needUpdate) {
      gridRef.current?.api.purgeInfiniteCache()
      resetNeedUpdate()
    }
  }, [gridRef, needUpdate, resetNeedUpdate])

  return (
    <>
      <TableToolbar title="History" />
      <Box height="800px">
        <InfinityAgGrid
          pagination
          innerRef={gridRef}
          columnDefs={columnDefs}
          onGridReady={onGridReady}
        />
      </Box>
    </>
  )
}
